/**
 * ro-buy-now.js - 立即购买公共脚本
 * 使用 Storefront API 创建临时购物车并直接跳转结账
 * 支持单个或多个产品立即购买
 * 
 * @version 1.0.0
 * @requires Storefront API Access Token
 * 
 * 使用方法：
 * 1. 单个产品：<button class="ro-buy-now" data-variant-id="123">Buy Now</button>
 * 2. 多个产品：<button class="ro-buy-now" data-variant-ids="123,456">Buy Now</button>
 * 
 * 可选属性：
 * - data-original-text: 按钮原始文本（默认："Buy Now"）
 * - data-loading-text: 加载中文本（默认："Processing..."）
 * - data-storefront-token: Storefront API Access Token（可选，默认使用全局配置）
 * - data-quantity: 商品数量（数字，不传则默认为 1）
 * - data-quantity-selector-id: 数量选择器容器 id（如 ro-quantity-selector 的 id），会从 window.RoQuantitySelectors[id].getValue() 读取当前数量，优先级低于 data-quantity
 */
(function() {
  'use strict';

  // 防止多次初始化
  if (window.__RoBuyNowInitialized__) {
    return;
  }
  window.__RoBuyNowInitialized__ = true;

  // 命名空间，避免全局冲突
  const RoBuyNow = {
    // 配置 - 默认值，可通过全局 window.RoBuyNowConfig 覆盖
    config: {
      storeDomain: typeof window.RoBuyNowConfig !== 'undefined' && window.RoBuyNowConfig.storeDomain 
        ? window.RoBuyNowConfig.storeDomain 
        : (typeof shop !== 'undefined' && shop.permanent_domain ? shop.permanent_domain : ''),
      storefrontAccessToken: typeof window.RoBuyNowConfig !== 'undefined' && window.RoBuyNowConfig.storefrontAccessToken
        ? window.RoBuyNowConfig.storefrontAccessToken
        : '',
      apiVersion: typeof window.RoBuyNowConfig !== 'undefined' && window.RoBuyNowConfig.apiVersion
        ? window.RoBuyNowConfig.apiVersion
        : '2025-10',
      requestTimeout: typeof window.RoBuyNowConfig !== 'undefined' && window.RoBuyNowConfig.requestTimeout
        ? window.RoBuyNowConfig.requestTimeout
        : 30000 // 30秒超时
    },

    // GraphQL Mutation - 创建购物车
    CREATE_CART_MUTATION: `
      mutation roBuyNowCartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
          }
          userErrors {
            field
            message
          }
        }
      }
    `,

    /**
     * 初始化
     */
    init: function() {
      // 如果 storeDomain 或 storefrontAccessToken 未配置，尝试从页面获取
      this.loadConfigFromPage();
      this.bindEvents();
    },

    /**
     * 从页面加载配置（兼容 Liquid 输出）
     */
    loadConfigFromPage: function() {
      // 尝试从 script[data-ro-buy-now-config] 标签读取配置
      const configScript = document.querySelector('script[data-ro-buy-now-config]');
      if (configScript) {
        try {
          const pageConfig = JSON.parse(configScript.textContent.trim());
          if (pageConfig.storeDomain) this.config.storeDomain = pageConfig.storeDomain;
          if (pageConfig.storefrontAccessToken) this.config.storefrontAccessToken = pageConfig.storefrontAccessToken;
          if (pageConfig.apiVersion) this.config.apiVersion = pageConfig.apiVersion;
        } catch (e) {
          console.warn('[RoBuyNow] Failed to parse config from page:', e);
        }
      }
    },

    /**
     * 绑定事件 - 使用事件委托避免重复绑定
     */
    bindEvents: function() {
      const self = this;
      // 使用事件委托，绑定到 document
      document.addEventListener('click', function(e) {
        const button = e.target.closest('.ro-buy-now');
        if (button) {
          e.preventDefault();
          self.handleBuyNowClick(button);
        }
      });

      window.addEventListener('pageshow', function(event) {
        if (!event.persisted) return;

        document.querySelectorAll('.ro-buy-now.is-loading').forEach(function(button) {
          self.setButtonLoading(button, false);
        });
      });
    },

    /**
     * 处理 Buy Now 按钮点击
     * @param {HTMLElement} button - 按钮元素
     */
    handleBuyNowClick: async function(button) {
      // 防止重复点击 - 检查按钮自身状态
      if (button.disabled || button.classList.contains('is-loading')) {
        return;
      }

      // 检查配置
      if (!this.config.storefrontAccessToken) {
        console.error('[RoBuyNow] Storefront Access Token is not configured.');
        this.showError('Checkout is not available. Please contact support.');
        return;
      }

      if (!this.config.storeDomain) {
        console.error('[RoBuyNow] Store domain is not configured.');
        this.showError('Checkout is not available. Please contact support.');
        return;
      }

      // 获取变体 ID（支持单个或多个）
      let variantIds = [];
      if (button.dataset.variantIds) {
        // 多个变体：逗号分隔
        variantIds = button.dataset.variantIds.split(',').map(function(id) {
          return id.trim();
        }).filter(Boolean);
      } else if (button.dataset.variantId) {
        // 单个变体
        variantIds = [button.dataset.variantId];
      }

      if (variantIds.length === 0) {
        console.error('[RoBuyNow] Variant ID(s) is missing.');
        this.showError('Product information is missing. Please refresh the page and try again.');
        return;
      }

      // 解析数量：data-quantity > data-quantity-selector-id 对应选择器 > 默认 1
      let quantity = 1;
      if (button.dataset.quantity !== undefined && button.dataset.quantity !== '') {
        const parsed = parseInt(button.dataset.quantity, 10);
        if (!isNaN(parsed) && parsed >= 1) quantity = parsed;
      } else if (button.dataset.quantitySelectorId) {
        const selectorId = button.dataset.quantitySelectorId;
        if (window.RoQuantitySelectors && typeof window.RoQuantitySelectors[selectorId] !== 'undefined') {
          const val = window.RoQuantitySelectors[selectorId].getValue();
          if (typeof val === 'number' && !isNaN(val) && val >= 1) quantity = val;
        }
      }

      // 设置 loading 状态
      this.setButtonLoading(button, true);

      try {
        // 创建临时购物车（带超时），传入数量
        const cart = await this.createCart(variantIds, quantity);

        if (cart && cart.checkoutUrl) {
          // 跳转到结账页面
          window.location.href = cart.checkoutUrl;
          // 跳转期间保留 loading，从浏览器缓存返回时由 pageshow 恢复
        } else {
          throw new Error('Failed to create cart or get checkout URL');
        }
      } catch (error) {
        console.error('[RoBuyNow] Error:', error);
        this.setButtonLoading(button, false);
        const errorMessage = error.message || 'Unable to proceed to checkout. Please try again.';
        this.showError(errorMessage);
      }
    },

    /**
     * 设置按钮 loading 状态
     * @param {HTMLElement} button - 按钮元素
     * @param {boolean} isLoading - 是否 loading
     */
    setButtonLoading: function(button, isLoading) {
      if (isLoading) {
        button.classList.add('is-loading');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = button.dataset.loadingText || 'Processing...';
      } else {
        button.classList.remove('is-loading');
        button.disabled = false;
        button.setAttribute('aria-busy', 'false');
        // 使用 data-original-text 恢复原始文本
        button.textContent = button.dataset.originalText || 'Buy Now';
      }
    },

    /**
     * 显示错误信息
     * @param {string} message - 错误信息
     */
    showError: function(message) {
      // 优先使用 Toast（如果存在）
      if (typeof showToast === 'function') {
        showToast(message);
        return;
      }
      // 否则使用 alert
      alert(message);
    },

    /**
     * 创建临时购物车（支持多个变体）
     * @param {Array<string>} variantIds - Shopify Variant ID 数组（数字形式）
     * @param {number} [quantity=1] - 每个变体的数量，不传则默认为 1
     * @returns {Promise<Object|null>} - 返回 cart 对象或 null
     */
    createCart: async function(variantIds, quantity) {
      const qty = typeof quantity === 'number' && !isNaN(quantity) && quantity >= 1 ? quantity : 1;
      // 转换为 Global ID 格式并构建 lines 数组
      const lines = variantIds.map(function(variantId) {
        return {
          merchandiseId: 'gid://shopify/ProductVariant/' + variantId,
          quantity: qty
        };
      });

      // 创建 AbortController 用于超时控制
      const controller = new AbortController();
      const timeoutId = setTimeout(function() {
        controller.abort();
      }, this.config.requestTimeout);

      try {
        const response = await fetch(
          'https://' + this.config.storeDomain + '/api/' + this.config.apiVersion + '/graphql.json',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': this.config.storefrontAccessToken
            },
            body: JSON.stringify({
              query: this.CREATE_CART_MUTATION,
              variables: {
                input: {
                  lines: lines
                }
              }
            }),
            signal: controller.signal
          }
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error('Network response was not ok: ' + response.status);
        }

        const result = await response.json();

        // 检查 GraphQL 错误
        if (result.errors && result.errors.length > 0) {
          console.error('[RoBuyNow] GraphQL errors:', result.errors);
          throw new Error(result.errors[0].message);
        }

        // 检查 userErrors
        const userErrors = result.data?.cartCreate?.userErrors;
        if (userErrors && userErrors.length > 0) {
          console.error('[RoBuyNow] User errors:', userErrors);
          throw new Error(userErrors[0].message);
        }

        return result.data?.cartCreate?.cart || null;
      } catch (error) {
        clearTimeout(timeoutId);
        
        // 处理超时错误
        if (error.name === 'AbortError') {
          throw new Error('Request timeout. Please check your network and try again.');
        }
        throw error;
      }
    }
  };

  // DOM Ready 后初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      RoBuyNow.init();
    });
  } else {
    RoBuyNow.init();
  }

  // 暴露到全局，方便调试和扩展
  window.RoBuyNow = RoBuyNow;
})();
